import {useEffect, useState} from 'react';
import {AlertCircle, CheckCircle2, Clock} from 'lucide-react';
import QuestionCard from './QuestionCard';
import Timer from './Timer';
import ResultModal from './ResultModal';
import {useParams} from "react-router-dom";
import {useApiReadStudentExam, useApiStudentAnswerExam} from "@/api/exam";
import {isNull} from "lodash";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import {sprintf} from "@/lib/utils/sprintf";
import {URLS} from "@/lib/urls";


const ExamPage = ({setBackUrl}) => {
    const [examData, setExamData] = useState()
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState({})
    const [examTime, setExamTime] = useState(null)
    const [timeRemaining, setTimeRemaining] = useState(120);
    const [isExamFinished, setIsExamFinished] = useState(false);
    const [first, setFirst] = useState(1)

    const params = useParams();
    const examId = params.id;

    const {data, isPending, isSuccess} = useApiReadStudentExam(examId)
    const {mutate: saveAnswer, isPending: isPendingSave} = useApiStudentAnswerExam()

    useEffect(() => {
        if (isSuccess) {
            setBackUrl(sprintf(URLS.student.lessons.myLesson.detail, {id: data?.data?.data?.takenunit_id}))

            setExamData(data?.data?.data)
            setExamTime(data?.data?.data?.setting?.time * 60)
            setTimeRemaining(data?.data?.data?.setting?.time * 60)
            setFirst(2)
        }
    }, [isSuccess])

    useEffect(() => {
        if (isNull(timeRemaining)) {
            return () => {
            }
        }
        if (timeRemaining <= 0 && !isExamFinished) {
            handleFinishExam();
            return () => {
            }
        }

        const timer = setInterval(() => {
            setTimeRemaining((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [timeRemaining, isExamFinished]);

    const handleAnswerSelect = (questionId, answer) => {
        setAnswers((prev) => ({...prev, [questionId]: answer}));
    };

    const handleNextQuestion = () => {
        if (examData && currentQuestionIndex < examData.resolved_questions?.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((prev) => prev - 1);
        }
    };

    const handleFinishExam = () => {
        setIsExamFinished(true);
        const newAnswers = Object.fromEntries(
            Object.entries(answers).map(([key, value]) => [`q${key}`, value])
        );
        saveAnswer({
            id: examId,
            answers: newAnswers,
        }, {
            onSuccess: (e) => {
                ApiHandleSuccessMessage(e)
                setResult(e?.data?.data)
            },
            onError: (e) => {
                ApiHandleErrorMessage(e)
            }
        })
    };

    const handleRestartExam = () => {
        setCurrentQuestionIndex(0);
        setAnswers({});
        setTimeRemaining(examTime);
        setIsExamFinished(false);
    };

    const getAnsweredCount = () => {
        return Object.keys(answers).length;
    };

    if (isPending) {
        return (
            <div className="text-center">
                <div
                    className="w-16 h-16 border-4 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300 text-lg">در حال بارگذاری آزمون...</p>
            </div>
        );
    }

    if (!examData) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
                <AlertCircle className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4"/>
                <p className="text-gray-800 dark:text-gray-100 text-xl">خطا در بارگذاری آزمون</p>
            </div>
        );
    }

    let currentQuestion = {}
    if (examData?.resolved_questions?.length > 0) {
        currentQuestion = examData?.resolved_questions[currentQuestionIndex];
    }
    const progress = ((currentQuestionIndex + 1) / examData.resolved_questions?.length) * 100;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl dark:shadow-black/20 overflow-hidden">
                <div className="bg-gradient-to-l from-blue-600 to-cyan-600 p-6 text-white">
                    <div className="flex items-center justify-between mb-4 flex-wrap">
                        <div>
                            <h1 className="text-3xl font-bold mb-2">
                                آزمون{' '}
                                {examData?.topic?.title}
                            </h1>
                            <p className="text-blue-100 dark:text-blue-200">
                                تعداد دفعات شرکت در آزمون {examData.level} |
                                تعداد سوالات{' '}
                                {examData.countquestion}
                            </p>
                        </div>
                        <Timer timeRemaining={timeRemaining}/>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5"/>
                            <span>پاسخ داده شده: {getAnsweredCount()} از {examData?.resolved_questions?.length}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5"/>
                            <span>سوال {currentQuestionIndex + 1} از {examData?.resolved_questions?.length}</span>
                        </div>
                    </div>
                </div>

                <div className="h-2 bg-gray-200 dark:bg-gray-700">
                    <div
                        className="h-full bg-gradient-to-l from-blue-500 to-cyan-500 transition-all duration-500 ease-out"
                        style={{width: `${progress}%`}}
                    ></div>
                </div>

                <div className="p-8">
                    <QuestionCard
                        question={currentQuestion}
                        selectedAnswer={answers[currentQuestion?.id]}
                        onAnswerSelect={(answer) => handleAnswerSelect(currentQuestion?.id, answer)}
                        questionNumber={currentQuestionIndex + 1}
                    />

                    <div className="mt-8 flex items-center justify-between gap-4">
                        <button
                            onClick={handlePreviousQuestion}
                            disabled={currentQuestionIndex === 0}
                            className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            سوال قبلی
                        </button>

                        <div className="hidden lg:flex gap-2 flex-wrap">
                            {examData?.resolved_questions?.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentQuestionIndex(index)}
                                    className={`w-10 h-10 rounded-lg font-medium transition-all ${
                                        index === currentQuestionIndex
                                            ? 'bg-blue-600 text-white scale-110 shadow-lg'
                                            : answers[examData?.resolved_questions[index]?.id]
                                                ? 'bg-green-500 text-white dark:bg-green-600'
                                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>

                        {currentQuestionIndex === examData.resolved_questions?.length - 1 ? (
                            <button
                                onClick={handleFinishExam}
                                className="px-8 py-3 bg-gradient-to-l from-green-500 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all"
                            >
                                اتمام آزمون
                            </button>
                        ) : (
                            <button
                                onClick={handleNextQuestion}
                                className="px-6 py-3 bg-gradient-to-l from-blue-600 to-cyan-600 text-white rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all"
                            >
                                سوال بعدی
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-black/20 p-6 hidden">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">نکات مهم:</h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span>پس از اتمام زمان، آزمون به صورت خودکار پایان می‌یابد.</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span>می‌توانید بین سوالات جابجا شوید و پاسخ‌های خود را تغییر دهید.</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span>سوالات پاسخ داده شده با رنگ سبز نمایش داده می‌شوند.</span>
                    </li>
                </ul>
            </div>

            {isExamFinished && (
                <ResultModal
                    result={result}
                    totalQuestions={examData.resolved_questions.length}
                    answeredQuestions={getAnsweredCount()}
                    timeSpent={examTime - timeRemaining}
                    onRestart={handleRestartExam}
                />
            )}
        </div>
    );
};

export default ExamPage;